import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Footer } from "../../layouts/footer";
import config from '../../../config';

const ViewUser = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${config.baseURL}/viewuserfront/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch user data. Status: ' + response.status);
        }
        const data = await response.json();

        // --- Crucial Change Here ---
        if (Array.isArray(data) && data.length > 0) {
          setProduct(data[0]); // Set the state with the first object from the array
        } else {
          // Handle cases where the array is empty or not an array
          setError('No user data found for this ID.');
          setProduct(null); // Ensure product is null if no data
        }
        // --- End of Crucial Change ---

      } catch (err) {
        console.error('Error fetching user:', err.message);
        setError('Could not load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-lg font-medium text-gray-700 dark:text-gray-300">
        Loading user data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-lg font-medium text-red-600 dark:text-red-400">
        Error: {error}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center h-screen text-lg font-medium text-gray-700 dark:text-gray-300">
        User not found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-6 p-4 md:p-6 bg-gray-100 min-h-screen dark:bg-gray-900">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
        View User Details
      </h1>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
            User Information
          </h2>
        </div>
        <div className="p-6">
          <form>
            <div className="space-y-8">
              <div className="border-b border-gray-900/10 pb-8">
                <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-6">
                  {/* First Name */}
                  <div className="sm:col-span-3">
                    <label htmlFor="fname" className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-300">
                      First Name
                    </label>
                    <div className="mt-2">
                      <input
                        id="fname"
                        name="fname"
                        type="text"
                        value={product.fname || ''}
                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:focus:ring-blue-500"
                        disabled
                      />
                    </div>
                  </div>
                  {/* Last Name */}
                  <div className="sm:col-span-3">
                    <label htmlFor="lname" className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-300">
                      Last Name
                    </label>
                    <div className="mt-2">
                      <input
                        id="lname"
                        name="lname"
                        type="text"
                        value={product.lname || ''}
                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:focus:ring-blue-500"
                        disabled
                      />
                    </div>
                  </div>

                  {/* Remaining Inputs - Dynamically mapped */}
                  {[
                    { label: 'Email', id: 'email', value: product.email },
                    { label: 'Company', id: 'company', value: product.company },
                    { label: 'Country', id: 'country', value: product.country },
                    { label: 'Address', id: 'address', value: product.address },
                    { label: 'City', id: 'city', value: product.city },
                    { label: 'Postcode', id: 'postcode', value: product.postcode },
                    { label: 'Phone', id: 'phone', value: product.phone },
                    { label: 'VAT', id: 'vat', value: product.vat },
                  ].map(({ label, id, value }) => (
                    <div key={id} className="sm:col-span-3">
                      <label htmlFor={id} className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-300">
                        {label}
                      </label>
                      <div className="mt-2">
                        <input
                          id={id}
                          name={id}
                          type="text"
                          value={value || ''}
                          className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:focus:ring-blue-500"
                          disabled
                        />
                      </div>
                    </div>
                  ))}

                  {/* Order Note (Product Description) */}
                  <div className="col-span-full">
                    <label htmlFor="note" className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-300">
                      Order Note
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="note"
                        name="order_note"
                        rows="4"
                        value={product.order_note || ''}
                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:focus:ring-blue-500"
                        disabled
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ViewUser;